import React, {
  useState,
} from "react";

import useMatrixMedia from "../hooks/useMatrixMedia";

import "./Message.css";

function formatTime(timestamp) {
  if (!timestamp) {
    return "";
  }

  return new Date(
    timestamp
  ).toLocaleTimeString(
    "ru-RU",
    {
      hour: "2-digit",
      minute: "2-digit",
    }
  );
}

function getFileName(content) {
  return (
    content?.filename ||
    content?.body ||
    "Файл"
  );
}

function getMediaType(content) {
  return content?.msgtype || "m.text";
}

function getCaption(content) {
  if (!content) {
    return "";
  }

  if (
    content.filename &&
    content.body &&
    content.body !== content.filename
  ) {
    return content.body;
  }

  return "";
}

/**
 * Отдельный компонент для обычных media:
 * image / video / audio
 */
function MediaLoader({
  client,
  mxcUrl,
  type,
  fileName,
  width,
  height,
  children,
}) {
  const {
    url,
    loading,
    error,
  } = useMatrixMedia(
    client,
    mxcUrl,
    {
      width,
      height,
      resizeMethod: "scale",
    }
  );

  if (loading) {
    return (
      <div className="chat-message__media-loading">
        Загрузка...
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-message__media-error">
        Не удалось загрузить{" "}
        {type === "file"
          ? "файл"
          : "медиа"}
      </div>
    );
  }

  if (!url) {
    return null;
  }

  return children(
    url,
    fileName
  );
}

/**
 * Скачивание Matrix media с Authorization.
 *
 * Важно:
 * нельзя просто сделать:
 *
 * <a href={matrixUrl} download>
 *
 * потому что Matrix server требует
 * Authorization: Bearer ...
 */
function FileDownload({
  client,
  mxcUrl,
  fileName,
}) {
  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  async function handleDownload() {
    if (
      !client ||
      !mxcUrl ||
      loading
    ) {
      return;
    }

    try {
      setLoading(true);
      setError("");

      const accessToken =
        client.getAccessToken?.();

      if (!accessToken) {
        throw new Error(
          "Matrix access token отсутствует"
        );
      }

      /**
       * Для файла не нужен thumbnail.
       *
       * Получаем обычный authenticated
       * download URL.
       */
      const downloadUrl =
        client.mxcUrlToHttp(
          mxcUrl,
          undefined,
          undefined,
          undefined,
          false,
          true,
          true
        );

      if (!downloadUrl) {
        throw new Error(
          "Не удалось получить URL файла"
        );
      }

      const response =
        await fetch(
          downloadUrl,
          {
            method: "GET",
            headers: {
              Authorization:
                `Bearer ${accessToken}`,
            },
          }
        );

      if (!response.ok) {
        let message =
          `HTTP ${response.status}`;

        try {
          const data =
            await response.json();

          if (data?.error) {
            message = data.error;
          }
        } catch {
          // Ответ не JSON
        }

        throw new Error(message);
      }

      const blob =
        await response.blob();

      /**
       * Создаём локальный blob URL.
       */
      const objectUrl =
        URL.createObjectURL(blob);

      /**
       * Создаём временную ссылку.
       *
       * download гарантирует,
       * что браузер попытается
       * сохранить файл.
       */
      const link =
        document.createElement("a");

      link.href = objectUrl;
      link.download =
        fileName || "download";

      link.style.display = "none";

      document.body.appendChild(link);

      link.click();

      link.remove();

      /**
       * Blob URL больше не нужен.
       */
      setTimeout(() => {
        URL.revokeObjectURL(
          objectUrl
        );
      }, 1000);
    } catch (err) {
      console.error(
        "[MATRIX MEDIA] Ошибка скачивания файла:",
        err
      );

      setError(
        err?.message ||
        "Не удалось скачать файл"
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="chat-message__file-download-wrapper">

      <button
        type="button"
        className="chat-message__file-link"
        onClick={handleDownload}
        disabled={loading}
      >
        <span className="chat-message__file-icon">
          {loading
            ? "⏳"
            : "📎"}
        </span>

        <span className="chat-message__file-name">
          {fileName}
        </span>

        <span className="chat-message__file-download">
          {loading
            ? "Загрузка..."
            : "Скачать"}
        </span>
      </button>

      {error && (
        <div className="chat-message__file-error">
          Не удалось загрузить файл
        </div>
      )}

    </div>
  );
}

export default function Message({
  event,
  client,
  isOwn,
}) {
  const sender =
    event?.getSender?.() ||
    "user";

  const content =
    event?.getContent?.() || {};

  const type =
    getMediaType(content);

  const text =
    content.body || "";

  const time =
    formatTime(
      event?.getTs?.()
    );

  const mxcUrl =
    content.url || "";

  const fileName =
    getFileName(content);

  const caption =
    getCaption(content);

  const mediaWidth =
    content.info?.w;

  const mediaHeight =
    content.info?.h;

  const messageClass = [
    "chat-message",
    isOwn
      ? "chat-message--own"
      : "chat-message--other",
  ]
    .filter(Boolean)
    .join(" ");

  const senderInitial =
    sender
      .replace(/^@/, "")
      .charAt(0)
      .toUpperCase();

  return (
    <article
      className={messageClass}
    >

      {!isOwn && (
        <div className="chat-message__avatar">
          {senderInitial}
        </div>
      )}

      <div className="chat-message__content">

        {!isOwn && (
          <div className="chat-message__meta">
            <strong>
              {sender}
            </strong>

            <span>
              {time}
            </span>
          </div>
        )}

        {isOwn && (
          <div className="chat-message__meta chat-message__meta--own">
            <span>
              {time}
            </span>
          </div>
        )}

        {type === "m.text" && (
          <div className="chat-message__text">
            {text}
          </div>
        )}

        {type === "m.image" && (
          <div className="chat-message__media">

            <MediaLoader
              client={client}
              mxcUrl={mxcUrl}
              type="image"
              fileName={fileName}
              width={mediaWidth}
              height={mediaHeight}
            >
              {(url, name) => (
                <>
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="chat-message__image-link"
                  >
                    <img
                      src={url}
                      alt={name}
                      className="chat-message__image"
                    />
                  </a>

                  {caption && (
                    <div className="chat-message__caption">
                      {caption}
                    </div>
                  )}
                </>
              )}
            </MediaLoader>

          </div>
        )}

        {type === "m.video" && (
          <div className="chat-message__media">

            <MediaLoader
              client={client}
              mxcUrl={mxcUrl}
              type="video"
              fileName={fileName}
            >
              {(url, name) => (
                <>
                  <video
                    src={url}
                    controls
                    preload="metadata"
                    className="chat-message__video"
                  />

                  <div className="chat-message__filename">
                    {name}
                  </div>
                </>
              )}
            </MediaLoader>

          </div>
        )}

        {type === "m.audio" && (
          <div className="chat-message__audio">

            <MediaLoader
              client={client}
              mxcUrl={mxcUrl}
              type="audio"
              fileName={fileName}
            >
              {(url) => (
                <audio
                  src={url}
                  controls
                />
              )}
            </MediaLoader>

            <span>
              🎙️ Голосовое сообщение
            </span>

          </div>
        )}

        {type === "m.file" && (
          <div className="chat-message__file">

            <FileDownload
              client={client}
              mxcUrl={mxcUrl}
              fileName={fileName}
            />

          </div>
        )}

        {![
          "m.text",
          "m.image",
          "m.video",
          "m.audio",
          "m.file",
        ].includes(type) && (
          <div className="chat-message__text">
            {text ||
              `Тип сообщения: ${type}`}
          </div>
        )}

      </div>

    </article>
  );
}